// src/pages/MindEaseHomePage.jsx — the front door.
//
// Built from what the app actually has: the eight modules (cards derived from
// the module files), the Vibe Check, the tools the modules deep-link into,
// Breeze, and the crisis numbers for the reader's own country. Replaces the
// Friendships Vibe home page the clone shipped with, and the unrouted
// MindEase draft that listed a module set that was never written.
import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, BookOpen, Wrench, MessageCircle, ShieldAlert,
  HeartPulse, Wind, ScanSearch, Thermometer, ClipboardCheck, Compass,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import EnhancedTeenModuleCard from '../components/modules/EnhancedTeenModuleCard';
import { mindeaseModulesData } from '../data/mindeaseModulesMetadata';
import { CRISIS_SUPPORT, initialCountry } from '../data/crisisSupport';

const FEATURED_TOOLS = [
  { id: 'panic-sos', name: 'Panic SOS', icon: HeartPulse, blurb: 'For the middle of one: the paced out-breath, grounding, a timer to watch the minutes, and the crisis numbers for your country.' },
  { id: 'breathe-like-you-mean-it', name: 'Breathe Like You Mean It', icon: Wind, blurb: 'The out-breath, properly — slow and small, not deep — and what it does and doesn’t do.' },
  { id: 'body-scan-detective', name: 'Body Scan Detective', icon: ScanSearch, blurb: 'A body map for where the alarm lands first. Most people have the same two places every time and have never looked.' },
  { id: 'anxiety-thermometer', name: 'Anxiety Thermometer', icon: Thermometer, blurb: 'Rate it out of ten, log what set it off, and watch your own pattern appear after a few entries.' },
];

const HOW_IT_WORKS = [
  { icon: ClipboardCheck, title: 'See what your alarm is doing', text: 'The Vibe Check is twenty-five questions with no score to beat. It points you at the module and the tools that fit where you are right now.' },
  { icon: BookOpen, title: 'Read one module at a time', text: 'Eight modules, five short parts each. One running story per module, every study named and told as it happened, and a place to sit with what came up.' },
  { icon: Wrench, title: 'Use the kit in the moment', text: 'Each module deep-links into the tool built for it. Breeze, the coach, has read every module, so you never have to explain the backstory.' },
];

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.4 } };

const MindEaseHomePage = () => {
  const { setCurrentView, getModuleProgress = () => ({ progressPercentage: 0 }), isModuleCompleted = () => false } = useApp();
  const country = CRISIS_SUPPORT[initialCountry()];
  const youthLine = country?.services.find((s) => s.youthFocused) || country?.services[0];

  const openModule = (id) => {
    try { localStorage.setItem('returnToModule', String(id)); } catch { /* storage unavailable */ }
    setCurrentView('modules');
  };
  const openTool = (id) => {
    try { localStorage.setItem('selectedTool', id); localStorage.removeItem('selectedToolMode'); } catch { /* storage unavailable */ }
    setCurrentView('tools');
  };

  return (
    <div className="min-h-screen bg-slate-900" data-page="home">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-28 sm:pt-36 pb-14">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: "url('/images/mindease-01.jpg')" }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/60 to-slate-900" aria-hidden="true" />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-5xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-vibe-ocean-light font-semibold">MindEase Vibe · for 13 to 17s</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight font-display">
              Turn the volume down on anxiety
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Your alarm goes off before you&apos;ve finished hearing the sentence. That&apos;s the design, not a fault. This app is about what happens in the ten minutes after — told through real studies, one running story, and tools built for the moment you actually need them.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCurrentView('vibe-assessment-intro')}
                className="inline-flex items-center gap-2 px-7 py-3 bg-gradient-to-r from-vibe-ocean to-vibe-twilight hover:from-vibe-twilight hover:to-vibe-ocean text-white font-semibold rounded-lg shadow-vibe-md transition-all focus:outline-none focus:ring-2 focus:ring-vibe-ocean/50"
              >
                <Sparkles aria-hidden="true" className="w-4 h-4" />
                Start with the Vibe Check
              </button>
              <button
                type="button"
                onClick={() => openModule(1)}
                className="inline-flex items-center gap-2 px-7 py-3 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-lg transition-all"
              >
                Go straight to Module 1
                <ArrowRight aria-hidden="true" className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-sm pt-2">
              {[
                'Studies told as they happened, with the honest caveat',
                'Nothing here is a treatment, and it says so',
                'Crisis numbers for your country, not someone else’s',
              ].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-200">{t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <motion.div {...fade} className="grid md:grid-cols-3 gap-5">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={step.title} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-full bg-vibe-ocean/15 border border-vibe-ocean/40 text-vibe-ocean-light text-sm font-bold flex items-center justify-center">{i + 1}</span>
                  <step.icon aria-hidden="true" className="w-5 h-5 text-vibe-ocean-light" />
                </div>
                <h2 className="text-white font-semibold text-lg mb-2 font-display">{step.title}</h2>
                <p className="text-slate-300 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-14 bg-gradient-to-b from-slate-900 to-purple-950/40">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <motion.div {...fade} className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-vibe-ocean-light font-semibold mb-2">Dive In</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-display">Eight modules</h2>
              <p className="text-slate-300 mt-2 max-w-2xl">Start with Module 1 if anxiety is new territory, or go straight to the one that matches what is going on for you. Each part stands on its own if you only have six minutes.</p>
            </div>
            <button type="button" onClick={() => setCurrentView('modules')} className="inline-flex items-center gap-2 text-vibe-ocean-light hover:text-white font-medium transition-colors">
              All eight modules
              <ArrowRight aria-hidden="true" className="w-4 h-4" />
            </button>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {mindeaseModulesData.slice(0, 3).map((module) => (
              <EnhancedTeenModuleCard
                key={module.id}
                module={module}
                isCompleted={isModuleCompleted(module.id)}
                canAccess
                progress={getModuleProgress(module.id)?.progressPercentage || 0}
                onClick={() => openModule(module.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-14 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <motion.div {...fade} className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-vibe-sage font-semibold mb-2">Your Toolkit</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-display">Built for the moment, not the reading</h2>
              <p className="text-slate-300 mt-2 max-w-2xl">Fourteen tools. The four the first modules lean on most are here; every module links straight into the one built for it.</p>
            </div>
            <button type="button" onClick={() => setCurrentView('tools')} className="inline-flex items-center gap-2 text-vibe-sage hover:text-white font-medium transition-colors">
              All the tools
              <ArrowRight aria-hidden="true" className="w-4 h-4" />
            </button>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURED_TOOLS.map((tool) => (
              <button
                key={tool.id}
                type="button"
                onClick={() => openTool(tool.id)}
                className="text-left p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-vibe-sage/50 hover:bg-white/[0.07] transition-all group"
                data-tool={tool.id}
              >
                <tool.icon aria-hidden="true" className="w-6 h-6 text-vibe-sage mb-3" />
                <h3 className="text-white font-semibold mb-1.5">{tool.name}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{tool.blurb}</p>
                <span className="inline-flex items-center gap-1 text-vibe-sage text-sm mt-3 group-hover:text-white transition-colors">Open <ArrowRight aria-hidden="true" className="w-3.5 h-3.5" /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Coach + safety */}
      <section className="py-14 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl grid md:grid-cols-2 gap-6">
          <motion.div {...fade} className="p-6 rounded-2xl border bg-slate-900/60" style={{ borderColor: '#7fbf7f55' }}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white font-bold shadow-md" style={{ backgroundColor: '#7fbf7f' }}>B</div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] font-semibold mb-1" style={{ color: '#7fbf7f' }}>Talk it through with Breeze</p>
                <p className="text-slate-200 leading-relaxed mb-3">Breeze has read all eight modules, so a moment can be talked through without the backstory. It is an AI, not a person, and it says so before you start; when it matters, it points you to real people.</p>
                <button type="button" onClick={() => setCurrentView('coaching')} className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-90 transition-opacity" style={{ backgroundColor: '#7fbf7f' }}>
                  Chat with Breeze
                  <MessageCircle aria-hidden="true" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
          <motion.div {...fade} className="p-6 rounded-2xl border border-vibe-gold/40 bg-vibe-gold/[0.06]" data-home-safety={country ? initialCountry() : 'none'}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-vibe-gold/15 border border-vibe-gold/40 flex items-center justify-center">
                <ShieldAlert aria-hidden="true" className="w-5 h-5 text-vibe-gold" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-vibe-gold mb-1">If it&apos;s more than an alarm</p>
                <p className="text-slate-200 leading-relaxed mb-3">If anxiety is stopping you doing ordinary things most days, or you&apos;re having thoughts of hurting yourself, that&apos;s beyond what an app is for. Tell an adult you trust. {country && youthLine ? (
                  <>In {country.name}, {youthLine.name} is <span className="text-white font-semibold">{youthLine.number}</span>; in an emergency, <span className="text-white font-semibold">{country.emergency.number}</span>.</>
                ) : null} The Resources page has the numbers for every country this app serves.</p>
                <button type="button" onClick={() => setCurrentView('resources')} className="inline-flex items-center gap-2 text-vibe-gold hover:text-white text-sm font-medium transition-colors">
                  <Compass aria-hidden="true" className="w-4 h-4" />
                  Numbers for your country
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MindEaseHomePage;
